/**
 * Renders an ordered list of CMS section blocks for the public site.
 * Unknown block types are silently skipped.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone, Mail, ArrowRight, Check,
  UserCheck, ClipboardCheck, PoundSterling, Handshake,
  Shield, Award, BookOpen, Car, Clock, MapPin, Users, ThumbsUp, Star, RefreshCw,
} from "lucide-react";
import { Button } from "./ui/button";
import EnquiryForm from "./EnquiryForm";

// Curated icon set so admins pick from a known list.
export const ICON_LIBRARY = {
  "user-check": UserCheck,
  "clipboard-check": ClipboardCheck,
  "pound-sterling": PoundSterling,
  "handshake": Handshake,
  "shield": Shield,
  "award": Award,
  "book-open": BookOpen,
  "car": Car,
  "clock": Clock,
  "map-pin": MapPin,
  "users": Users,
  "thumbs-up": ThumbsUp,
  "star": Star,
  "refresh": RefreshCw,
  "check": Check,
  "phone": Phone,
  "mail": Mail,
};

function SectionRichtext({ section }) {
  return (
    <section className="py-12 md:py-16" data-testid={`section-richtext`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {section.title && (
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-6"
              style={{fontFamily:"Outfit, sans-serif"}}>{section.title}</h2>
        )}
        <div className="prose prose-neutral max-w-none" dangerouslySetInnerHTML={{ __html: section.body || "" }} />
      </div>
    </section>
  );
}

function SectionIntro({ section }) {
  const hasImage = !!section.image;
  return (
    <section className="py-12 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="section-intro">
      <div className={`grid ${hasImage ? "md:grid-cols-12" : ""} gap-10 items-start`}>
        {hasImage && (
          <div className="md:col-span-5">
            <img src={section.image} alt={section.image_alt || section.title || ""}
                 className="rounded-3xl border border-black/5 w-full aspect-[4/5] object-cover" />
          </div>
        )}
        <div className={hasImage ? "md:col-span-7" : ""}>
          {section.eyebrow && (
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">{section.eyebrow}</span>
          )}
          {section.title && (
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium mt-3"
                style={{fontFamily:"Outfit, sans-serif"}}>{section.title}</h2>
          )}
          <div className="mt-5 prose prose-neutral max-w-none" dangerouslySetInnerHTML={{ __html: section.body || "" }} />
        </div>
      </div>
    </section>
  );
}

function SectionImage({ section }) {
  if (!section.src) return null;
  return (
    <section className="py-8 md:py-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="section-image">
      <figure>
        <img src={section.src} alt={section.alt || ""} className="w-full rounded-3xl border border-black/5" />
        {section.caption && <figcaption className="mt-3 text-sm text-[#9CA3AF] text-center">{section.caption}</figcaption>}
      </figure>
    </section>
  );
}

function SectionSteps({ section }) {
  const items = section.items || [];
  if (items.length === 0) return null;
  return (
    <section className="py-12 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="section-steps">
      {section.title && (
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium mb-10"
            style={{fontFamily:"Outfit, sans-serif"}}>{section.title}</h2>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((s, i) => (
          <div key={i} className="bg-white border border-black/5 rounded-2xl p-6 hover:-translate-y-1 transition-transform">
            {s.step && <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#37B160]">Step {s.step}</div>}
            <h3 className="text-lg font-medium mt-3" style={{fontFamily:"Outfit, sans-serif"}}>{s.title}</h3>
            {s.body && <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{s.body}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionCta({ section }) {
  return (
    <section className="py-12 md:py-16" data-testid="section-cta">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#111827] text-white rounded-3xl p-8 md:p-12 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            {section.title && (
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{section.title}</h2>
            )}
            {section.subtitle && (
              <p className="mt-2 text-white/80">{section.subtitle}</p>
            )}
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to={section.cta_to || "/book"}>
              <Button className="bg-[#37B160] hover:bg-[#2D9450] text-white h-11 px-6">
                {section.cta_label || "Book your lessons"} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionContact({ section }) {
  return (
    <section className="py-12 md:py-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="section-contact-block">
      {section.title && (
        <h2 className="text-2xl sm:text-3xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{section.title}</h2>
      )}
      {section.body && <p className="mt-3 text-[#4B5563] leading-relaxed max-w-2xl">{section.body}</p>}
      <div className="mt-6 space-y-3">
        {section.phone && (
          <a href={`tel:${section.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-[#111827] hover:text-[#2D9450]">
            <Phone className="h-5 w-5 text-[#37B160]" /> <span className="text-lg" style={{fontFamily:"Outfit, sans-serif"}}>{section.phone}</span>
          </a>
        )}
        {section.email && (
          <a href={`mailto:${section.email}`} className="flex items-center gap-3 text-[#111827] hover:text-[#2D9450]">
            <Mail className="h-5 w-5 text-[#37B160]" /> <span className="text-lg" style={{fontFamily:"Outfit, sans-serif"}}>{section.email}</span>
          </a>
        )}
      </div>
    </section>
  );
}

function SectionFeatureList({ section }) {
  const items = section.items || [];
  if (items.length === 0) return null;
  return (
    <section className="py-12 md:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="section-features">
      {section.title && (
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-8"
            style={{fontFamily:"Outfit, sans-serif"}}>{section.title}</h2>
      )}
      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((f, i) => (
          <li key={i} className="flex items-start gap-3 bg-white border border-black/5 rounded-2xl p-5">
            <Check className="h-5 w-5 text-[#37B160] mt-0.5 flex-none" />
            <div>
              <div className="font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{f.title}</div>
              {f.body && <p className="mt-1 text-sm text-[#4B5563]">{f.body}</p>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SectionEnquiryForm({ section }) {
  return (
    <section className="py-12 md:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="section-enquiry-form">
      <div className="grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          {section.eyebrow && (
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">{section.eyebrow}</span>
          )}
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium mt-3"
              style={{fontFamily:"Outfit, sans-serif"}}>{section.title || "Make an enquiry"}</h2>
          {section.body && (
            <p className="mt-5 text-[#4B5563] leading-relaxed">{section.body}</p>
          )}
          <div className="mt-6 space-y-2 text-sm text-[#4B5563]">
            {section.phone && (
              <a href={`tel:${section.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-[#111827]">
                <Phone className="h-4 w-4 text-[#37B160]" /> {section.phone}
              </a>
            )}
            {section.email && (
              <a href={`mailto:${section.email}`} className="flex items-center gap-2 hover:text-[#111827]">
                <Mail className="h-4 w-4 text-[#37B160]" /> {section.email}
              </a>
            )}
          </div>
        </div>
        <div className="md:col-span-7">
          <EnquiryForm testIdPrefix="about-enquiry" />
        </div>
      </div>
    </section>
  );
}

function SectionTwoCol({ section }) {
  return (
    <section className="py-12 md:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="section-two-col">
      {section.title && (
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-8"
            style={{fontFamily:"Outfit, sans-serif"}}>{section.title}</h2>
      )}
      <div className="grid md:grid-cols-2 gap-10 items-start">
        <div className="prose prose-neutral max-w-none" dangerouslySetInnerHTML={{ __html: section.left || "" }} />
        <div className="prose prose-neutral max-w-none" dangerouslySetInnerHTML={{ __html: section.right || "" }} />
      </div>
    </section>
  );
}

function SectionIconFeatures({ section }) {
  const items = section.items || [];
  if (items.length === 0) return null;
  return (
    <section className="py-12 md:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="section-icon-features">
      {section.title && (
        <h2 className="text-2xl sm:text-3xl tracking-tight font-medium mb-10 text-center"
            style={{fontFamily:"Outfit, sans-serif"}}>{section.title}</h2>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 items-start">
        {items.map((f, i) => {
          const Ico = ICON_LIBRARY[f.icon] || Check;
          return (
            <div key={i} className="flex items-center gap-4">
              <Ico className="h-12 w-12 md:h-14 md:w-14 text-[#37B160] flex-none" strokeWidth={1.5} />
              <div className="text-sm md:text-base font-medium text-[#111827] leading-snug" style={{fontFamily:"Outfit, sans-serif"}}>
                {f.title}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SectionChecklist({ section }) {
  const items = section.items || [];
  if (items.length === 0) return null;
  return (
    <section className="py-12 md:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="section-checklist">
      {section.title && (
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium"
            style={{fontFamily:"Outfit, sans-serif"}}>{section.title}</h2>
      )}
      {section.subtitle && (
        <p className="mt-2 text-[#4B5563]">{section.subtitle}</p>
      )}
      <ul className="mt-8 grid md:grid-cols-2 gap-x-10 gap-y-4">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#37B160] flex-none mt-0.5" aria-hidden="true">
              <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
            </span>
            <span className="text-[#111827]">{typeof item === "string" ? item : item.title}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const RENDERERS = {
  richtext: SectionRichtext,
  intro: SectionIntro,
  image: SectionImage,
  steps: SectionSteps,
  cta: SectionCta,
  contact: SectionContact,
  features: SectionFeatureList,
  enquiry_form: SectionEnquiryForm,
  two_col: SectionTwoCol,
  icon_features: SectionIconFeatures,
  checklist: SectionChecklist,
};

export default function PageSections({ sections = [] }) {
  return (
    <>
      {sections.map((s, i) => {
        const Cmp = RENDERERS[s.type];
        if (!Cmp) return null;
        return <Cmp key={i} section={s} />;
      })}
    </>
  );
}

export { RENDERERS };
